import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Bold, Italic, Link2, List, Heading2, Image as ImageIcon, MousePointerClick,
  Braces, Eraser,
} from "lucide-react";

const cmd = (name, value) => document.execCommand(name, false, value);

const BTN_HTML = (label, href) =>
  `<p style="margin:0 0 16px"><a href="${href}" style="display:inline-block;padding:11px 20px;` +
  `border-radius:999px;background:#2563EB;color:#FFFFFF;text-decoration:none;font-weight:600;` +
  `font-size:14px">${label}</a></p>`;

const ToolButton = ({ label, testid, onClick, children }) => (
  <button type="button" title={label} data-testid={testid} onClick={onClick}
          className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground">
    {children}
  </button>
);

/** Visual | HTML | Preview editor. Plain text is derived server-side. */
export const CampaignEditor = ({ value, onChange, mergeTags = [], contactId }) => {
  const [mode, setMode] = useState(value.editor_mode || "visual");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const surface = useRef(null);
  const loadedHtml = useRef(null);

  // Only push HTML into the contentEditable when it changes from the outside
  useEffect(() => {
    if (mode !== "visual" || !surface.current) return;
    if (loadedHtml.current === value.content_html) return;
    surface.current.innerHTML = value.content_html || "";
    loadedHtml.current = value.content_html;
  }, [mode, value.content_html]);

  const setMode2 = (m) => {
    setMode(m);
    onChange({ editor_mode: m });
    if (m === "preview") runPreview();
  };

  const syncFromSurface = () => {
    const html = surface.current?.innerHTML || "";
    loadedHtml.current = html;
    onChange({ content_html: html });
  };

  const insertHtml = (html) => {
    if (mode === "visual") {
      surface.current?.focus();
      cmd("insertHTML", html);
      syncFromSurface();
    } else {
      onChange({ content_html: `${value.content_html || ""}${html}` });
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/campaigns/preview", {
        content_html: value.content_html || "",
        subject: value.subject || "",
        use_branding: value.use_branding !== false,
        contact_id: contactId || null,
      });
      setPreview(data);
    } catch {
      setPreview({ html: "<p>Preview unavailable</p>", subject: value.subject });
    } finally { setPreviewing(false); }
  };

  return (
    <div className="space-y-3" data-testid="campaign-editor">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={mode} onValueChange={setMode2}>
          <TabsList data-testid="editor-mode-tabs">
            <TabsTrigger value="visual" data-testid="editor-mode-visual">Visual</TabsTrigger>
            <TabsTrigger value="html" data-testid="editor-mode-html">HTML</TabsTrigger>
            <TabsTrigger value="preview" data-testid="editor-mode-preview">Preview</TabsTrigger>
          </TabsList>
        </Tabs>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" data-testid="merge-tag-menu">
              <Braces className="mr-1.5 h-3.5 w-3.5" /> Personalise
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {mergeTags.map((t) => (
              <DropdownMenuItem key={t.tag} data-testid={`merge-tag-${t.tag.replace(/[^a-z_]/g, "")}`}
                                onClick={() => insertHtml(t.fallback ? `{{${t.tag.replace(/[{}]/g, "")}|${t.fallback}}}` : t.tag)}>
                <span className="font-medium">{t.label}</span>
                <span className="ml-auto text-[10px] text-muted-foreground">{t.tag}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <label className="ml-auto flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <Switch data-testid="use-branding-toggle" checked={value.use_branding !== false}
                  onCheckedChange={(v) => onChange({ use_branding: v })} />
          Use business branding
        </label>
      </div>

      {mode === "visual" && (
        <>
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-border p-2">
            <ToolButton label="Bold" testid="tool-bold" onClick={() => { cmd("bold"); syncFromSurface(); }}><Bold className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Italic" testid="tool-italic" onClick={() => { cmd("italic"); syncFromSurface(); }}><Italic className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Heading" testid="tool-heading" onClick={() => { cmd("formatBlock", "<h2>"); syncFromSurface(); }}><Heading2 className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Bullet list" testid="tool-list" onClick={() => { cmd("insertUnorderedList"); syncFromSurface(); }}><List className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Link" testid="tool-link" onClick={() => {
              const href = window.prompt("Link URL", "https://");
              if (href) { cmd("createLink", href); syncFromSurface(); }
            }}><Link2 className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Image" testid="tool-image" onClick={() => {
              const src = window.prompt("Image URL", "https://");
              if (src) insertHtml(`<img src="${src}" alt="" style="max-width:100%;border-radius:8px">`);
            }}><ImageIcon className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Button" testid="tool-button" onClick={() => {
              const label = window.prompt("Button label", "Read more");
              if (!label) return;
              const href = window.prompt("Button URL", "https://") || "#";
              insertHtml(BTN_HTML(label, href));
            }}><MousePointerClick className="h-3.5 w-3.5" /></ToolButton>
            <ToolButton label="Clear formatting" testid="tool-clear" onClick={() => { cmd("removeFormat"); syncFromSurface(); }}><Eraser className="h-3.5 w-3.5" /></ToolButton>
          </div>
          <div ref={surface} contentEditable suppressContentEditableWarning
               data-testid="visual-editor-surface"
               onInput={syncFromSurface} onBlur={syncFromSurface}
               className="campaign-surface min-h-[260px] rounded-lg border border-border bg-background p-5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[hsl(var(--studio-primary)/0.35)]" />
          <p className="text-[11px] text-muted-foreground">
            Plain-text version is generated automatically when the campaign sends.
          </p>
        </>
      )}

      {mode === "html" && (
        <>
          <Label className="text-xs">HTML source</Label>
          <Textarea data-testid="html-editor" rows={16} spellCheck={false}
                    className="font-mono text-xs"
                    value={value.content_html || ""}
                    onChange={(e) => { loadedHtml.current = e.target.value; onChange({ content_html: e.target.value }); }} />
        </>
      )}

      {mode === "preview" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] font-normal">
              Subject: {preview?.subject || value.subject || "—"}
            </Badge>
            {preview?.contact_name && (
              <Badge variant="outline" className="text-[10px] font-normal" data-testid="preview-contact">
                Personalised for {preview.contact_name}
              </Badge>
            )}
            <Button type="button" variant="ghost" size="sm" className="ml-auto"
                    data-testid="preview-refresh" onClick={runPreview}>
              {previewing ? "Rendering…" : "Refresh"}
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-border bg-white">
            <iframe title="Campaign preview" data-testid="preview-frame"
                    srcDoc={preview?.html || "<p style='font:14px sans-serif;padding:20px'>Rendering…</p>"}
                    className="h-[420px] w-full border-0" />
          </div>
        </div>
      )}
    </div>
  );
};

export const TemplatePicker = ({ starters = [], mine = [], onPick, onDeleteMine, onRenameMine, onDuplicateMine }) => {
  const [tab, setTab] = useState("starters");
  const items = tab === "starters" ? starters : mine;
  return (
    <div className="space-y-3" data-testid="template-picker">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="starters" data-testid="template-tab-starters">Starter templates</TabsTrigger>
          <TabsTrigger value="mine" data-testid="template-tab-mine">My templates ({mine.length})</TabsTrigger>
        </TabsList>
      </Tabs>
      {items.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nothing saved yet — build a campaign and hit “Save as template”.
        </p>
      ) : (
        <div className="grid max-h-[320px] gap-2 overflow-y-auto sm:grid-cols-2">
          {items.map((t) => (
            <div key={t.id} className="rounded-xl border border-border p-4 hover:border-[hsl(var(--studio-primary)/0.45)]"
                 data-testid={`template-card-${t.id}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{t.name}</div>
                  <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">{t.category}</div>
                </div>
              </div>
              {t.description && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{t.description}</p>}
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Button size="sm" data-testid={`template-use-${t.id}`} onClick={() => onPick(t)}>Use</Button>
                {tab === "mine" && (
                  <>
                    <Button size="sm" variant="ghost" data-testid={`template-rename-${t.id}`}
                            onClick={() => {
                              const name = window.prompt("Template name", t.name);
                              if (name && name !== t.name) onRenameMine(t, name);
                            }}>Rename</Button>
                    <Button size="sm" variant="ghost" data-testid={`template-duplicate-${t.id}`}
                            onClick={() => onDuplicateMine(t)}>Duplicate</Button>
                    <Button size="sm" variant="ghost" data-testid={`template-delete-${t.id}`}
                            onClick={() => { if (window.confirm(`Delete “${t.name}”?`)) onDeleteMine(t); }}>Delete</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BLANK_TEMPLATE = {
  id: "blank",
  name: "Start from blank",
  category: "general campaign",
  subject: "",
  content_html: "<p>Hi {{first_name|there}},</p><p></p>",
};
